import * as PIXI from 'pixi.js';
import { EventEmitter } from 'events';
import ShopActions from '~/flux/actions/ShopActions';
import ShopStore from '~/flux/stores/ShopStore';
import GameItemIcon from '~/components/common/GameItemIcon';
import EquipmentDisplay from '~/components/common/EquipmentDisplay';
import CanvasTools from '~/view/CanvasTools';
import { Colors, Config } from '~/constants';
import text from '~/text';

const ShopInventoryShelves = function ({ onItemPurchaseConfirmed } = {}) {
  PIXI.Container.call(this);

  this.event_emitter = new EventEmitter();

  this.dispose = () => {
    this.event_emitter.removeAllListeners();
    for (const icon of _icons) {
      icon?.dispose();
    }
    _icons = null;
    destroyBuyButton();
    DT_CANVAS_GLOBALS.poolSafeDestroy(this, { children: true });
  };

  let _shopInventory = ShopStore.getAll().shopInventory;
  let _itemToBuy;
  let _icons = [];
  let _buyBtn;
  const myWidth = window.innerWidth * 0.42;

  const hideBuyButton = (this.hidePopups = () => {
    if (_buyBtn) {
      TweenMax.to(_buyBtn.scale, 0.15, {
        x: 0,
        y: 0,
        onComplete: destroyBuyButton,
        onCompleteParams: [_buyBtn],
      });
    }
  });

  const destroyBuyButton = () => {
    DT_CANVAS_GLOBALS.poolSafeDestroy(_buyBtn, { children: true });
  };

  const makeBuyButton = (event) => {
    destroyBuyButton();

    var btn = (_buyBtn = new PIXI.Sprite());
    btn.texture = PIXI.utils.TextureCache['armory/equip_arrow.png'];
    btn.anchor.x = btn.anchor.y = 0.5;

    btn.width = 78;
    btn.scale.y = btn.scale.x;

    // flip it horizontally, so it points right
    btn.scale.x *= -1;

    var unequipText = new PIXI.Text(text('ui.buy'), {
      fontFamily: 'Courier New',
      fontWeight: 'bold',
      fontSize: '36px',
      fill: 0x00ff00,
      dropShadow: true,
      dropShadowDistance: 4,
      align: 'center',
    });
    unequipText.anchor.x = unequipText.anchor.y = 0.5;
    btn.addChild(unequipText);

    // apparently we have to flip this too
    unequipText.scale.x *= -1;

    TweenMax.from(btn.scale, 0.2, { x: 0, y: 0 });

    const item_icon = event.target;
    item_icon.addChild(btn);

    // make sure our game item icon is ontop of other icons
    item_icon.parent.addChild(item_icon);

    btn.interactive = btn.buttonMode = true;
    btn.tap = btn.click = () => {
      event.stopPropagation();

      CanvasTools.makeFaerieSpinners(item_icon, 8);

      hideBuyButton();
      ShopActions.confirmItemPurchase(_itemToBuy);
      onItemPurchaseConfirmed();
      for (const icon of _icons) {
        icon?.getTooltip()?.dispose();
      }
    };

    _itemToBuy = item_icon.getItem();
  };

  const onItemIconClick = (event) => {
    makeBuyButton(event);
  };

  // init - create two shelves
  const shelf1 = new PIXI.Graphics();
  shelf1.beginFill(0xffffff);
  shelf1.drawRect(0, 0, myWidth, 3);
  shelf1.endFill();
  shelf1.cacheAsBitmap = true;
  shelf1.x = -window.innerWidth * 0.008;
  shelf1.y = window.innerHeight * 0.12;
  this.addChild(shelf1);

  const shelf2 = new PIXI.Graphics();
  shelf2.beginFill(0xffffff);
  shelf2.drawRect(0, 0, myWidth, 3);
  shelf2.endFill();
  shelf2.cacheAsBitmap = true;
  shelf2.x = -window.innerWidth * 0.008;
  shelf2.y = window.innerHeight * 0.25; // Second shelf lower
  this.addChild(shelf2);

  // init GameItemIcons
  for (let i = 0; i < _shopInventory.length; ++i) {
    if (!_shopInventory[i]) {
      continue;
    }

    var icon = new GameItemIcon(_shopInventory[i], {
      frame: false,
      showBlackwash: false,
    });
    icon.setScale(55);

    // Determine which shelf this item belongs to (0-6 = shelf 1, 7-13 = shelf 2)
    const itemsPerShelf = 7;
    const shelfIndex = Math.floor(i / itemsPerShelf);
    const positionOnShelf = i % itemsPerShelf;

    // Position horizontally based on position within shelf
    icon.x = ((positionOnShelf + 0.82) * myWidth) / (itemsPerShelf + 1);

    // Base Y position
    const myShelf = shelfIndex === 0 ? shelf1 : shelf2;
    icon.y = myShelf.y + DT_CANVAS_GLOBALS.spacing * 1.5 -
      icon.img.height / 3 -
      55 * (1 - icon.img.scale.y) * (1 - icon.img.anchor.y);

    // do some custom nudging to make the items look like they're sitting on the shelf
    const item = icon.getItem();
    const { slot, type } = item;
    const quality_index = EquipmentDisplay.getQualityIndex(item);
    if (slot === 'helmet') {
      switch (type) {
        case 'light':
          switch (quality_index) {
            case 1:
              icon.y += 18;
              break;

            case 2:
              icon.y += 20;
              break;

            case 3:
              icon.y += 23;
              break;

            case 4:
              icon.y += 18;
              break;

            default:
              icon.y += 12;
          }
          break;

        case 'med':
          switch (quality_index) {
            case 0:
              icon.y += 12;
              break;

            case 3:
              icon.y -= 1;
              break;

            case 4:
              icon.y += 7;
              break;
          }
          break;

        case 'heavy':
          switch (quality_index) {
            case 1:
              icon.y += 8;
              break;

            case 2:
              icon.y += 4;
              break;

            default:
              icon.y += 5;
          }
          break;
      }
    } else if (slot === 'torsoe') {
      if (type === 'light' && quality_index === 0) {
        icon.y += 8;
      } else {
        icon.y += 3;
      }
    } else if (slot === 'boots') {
      switch (type) {
        case 'light':
          icon.y += 7;
          break;

        default:
          icon.y += 3;
      }
    } else if (slot === 'weapon') {
      switch (type) {
        case 'sword':
          icon.y += 28;
          break;

        case 'hammer':
          icon.y += 26;
          break;

        case 'staff':
          icon.y += 10;
          break;

        case 'dagger':
          switch (quality_index) {
            case 3:
              icon.y += 20;
              break;

            default:
              icon.y += 15;
          }
          break;
      }
    } else if (slot === 'off_hand') {
      switch (type) {
        case 'shield':
          icon.y += 3;
          break;

        case 'bow':
          icon.y += 13;
          break;

        case 'trinket':
          switch (quality_index) {
            case 3:
              icon.y += 19;
              break;

            default:
              icon.y += 16;
          }
          break;

        case 'book':
          switch (quality_index) {
            case 1:
              icon.y += 7;
              break;

            case 4:
              icon.y += 14;
              break;

            default:
              icon.y += 10;
          }
      }
    } else if (slot === 'ring') {
      icon.y += 12;
    } else {
      switch (type) {
        case 'hero_xp_boost':
          icon.y += 4;
          break;

        case 'prayer_candle':
          icon.y += 5;
          break;

        case 'equipment_dye':
        case 'miracle_dye':
          icon.y += 5;
          break;

        case 'bag':
          icon.y += 3;
          break;
      }
    }

    // Only make clickable if not sold
    if (!item.sold) {
      (icon as any).tap = (icon as any).click = onItemIconClick;
    } else {
      // Dim sold items
      icon.alpha = 0.23;
      icon.interactive = icon.buttonMode = false;
    }

    this.addChild(icon);
    _icons.push(icon);

    const price_tag = new PIXI.Container();
    const priceTag_bg = new PIXI.Sprite();
    if (item.sold) {
      // Grey tint for sold items
      priceTag_bg.tint = 0x888888;
    } else if (item.gold_price) {
      priceTag_bg.tint = Colors.GOLD;
    } else if (item.pd_price) {
      priceTag_bg.tint = Colors.PIXIE_DUST;
    }
    priceTag_bg.texture = PIXI.utils.TextureCache['shop/price_tag.png'];
    priceTag_bg.width = Math.min(60, window.innerWidth * 0.04);
    priceTag_bg.scale.y = priceTag_bg.scale.x;
    price_tag.addChild(priceTag_bg);

    const priceText = new PIXI.Text(
      item.sold
        ? text('ui.shop.sold')
        : Intl.NumberFormat(Config.LOCALE, {
          maximumFractionDigits: 0,
          notation: 'standard',
        }).format(item.gold_price || item.pd_price),
      {
        fontFamily: 'Courier New',
        fontSize: 12,
        fontStyle: 'bold',
        fill: 0x000000,
      }
    );

    priceText.width = Math.min(priceText.width, priceTag_bg.width * 0.9);
    priceText.scale.y = priceText.scale.x;

    priceText.x = price_tag.width / 2 - priceText.width / 2;
    priceText.y = price_tag.height / 2 - priceText.height / 2;
    price_tag.addChild(priceText);

    price_tag.x = icon.x - price_tag.width / 2;
    // Adjust price tag Y position based on which shelf
    price_tag.y = myShelf.y;
    this.addChildAt(price_tag, 0);
  }
};
ShopInventoryShelves.prototype = Object.create(PIXI.Container.prototype);
ShopInventoryShelves.prototype.constructor = ShopInventoryShelves;
export default ShopInventoryShelves;
